/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button, HStack, Radio, RadioGroup } from '@chakra-ui/react';
import classNames from 'classnames';
import React from 'react';
import { FaRegQuestionCircle } from 'react-icons/fa';
import { FaTrashCan } from 'react-icons/fa6';
import Select from 'react-select';

import styles from '@/components/view-editor/styles.module.scss';
import { SORTING_ORDER } from '@/constants/ui';
import { ELEMENT_DATA_TEST_IDS } from '@/tests/testConstants';
import { MultiSortColumn, ViewLayoutListColumns } from '@/types/view.types';

type SortableItemsProps = {
  customDisplayNameMap?: Partial<Record<ViewLayoutListColumns, string>>;
  disableDelete: boolean;
  handleDelete: (value: ViewLayoutListColumns) => void;
  handleOrderChange: (selectedRule: MultiSortColumn, sortDirection: SORTING_ORDER) => void;
  handleUpdate: (selectedValue: MultiSortColumn, oldRuleValue: ViewLayoutListColumns) => void;
  index: number;
  rule: MultiSortColumn;
  sortByOptions: MultiSortColumn[];
};

const ViewLayoutSortableItem = (props: SortableItemsProps): React.JSX.Element => {
  const { customDisplayNameMap, rule, sortByOptions, handleUpdate, handleOrderChange, handleDelete, disableDelete, index } = props;
  const [selectedRule, setSelectedRule] = React.useState(rule);

  const onChange = (newRule: any) => {
    // we need the old rule info to find the current position of the updated rule,
    // so the new rule has the same position
    const oldRuleValue = selectedRule.columnValue;
    handleUpdate(newRule, oldRuleValue);
    setSelectedRule(newRule);
  };

  const onOrderChange = (sortDirection: SORTING_ORDER) => {
    const updatedOption = {
      ...selectedRule,
      sortDirection,
    };
    handleOrderChange(updatedOption, sortDirection);
    setSelectedRule(updatedOption);
  };

  const isAssignmentOrPersonnel =
    selectedRule.columnValue === ViewLayoutListColumns.ASSIGNMENT ||
    selectedRule.columnValue === ViewLayoutListColumns.PERSONNEL;

  const CUSTOM_SORT_TOOLTIP =
    `Custom sort ordering must be set up on the ${selectedRule.columnValue === ViewLayoutListColumns.ASSIGNMENT ? 'Assignments' : 'Personnel'} tab within the View Manager.`;

  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <Box>
      <HStack
        alignItems={'center'}
        border-color={'#efefef'}
        borderBottomWidth={'1px'}
        borderTopWidth={'1px'}
        display={'flex'}
        justifyContent={'space-between'}
        padding={'20px'}
        w={'800px'}
      >
        {/* eslint-disable-next-line no-magic-numbers */}
        <span>{index === 0 ? 'Sort By' : 'then by'}</span>
        <div className={styles.ruleListSelect} onClick={stopPropagation} onChange={(e) => e.stopPropagation()}>
          <Select
            className={classNames(styles.ruleListSelect)}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            menuPosition="fixed"
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                width: 250,
              }),
              menuPortal: (base) => ({ ...base, zIndex: 2500 }),
            }}
            options={sortByOptions}
            getOptionLabel={(option) => customDisplayNameMap?.[option.columnValue] ?? option.columnValue}
            getOptionValue={(option) => option.columnValue}
            onChange={onChange}
            value={selectedRule}
          />
        </div>
        <Box onClick={stopPropagation} style={{
          marginRight: isAssignmentOrPersonnel ? undefined : '170px',
          width: isAssignmentOrPersonnel ? '330px' : '160px',
        }}>
          <RadioGroup
            value={selectedRule.sortDirection}
            onChange={onOrderChange}
            className={classNames(styles.sortOrderRadioGroup)}
            width={'inherit'}
            margin={'unset'}
          >
            <HStack gap="6">
              <Radio value={SORTING_ORDER.ASC}>A to Z</Radio>
              <Radio data-testid={ELEMENT_DATA_TEST_IDS.DESC_LABEL} value={SORTING_ORDER.DESC}>
                Z to A
              </Radio>
              {isAssignmentOrPersonnel && (
                <Radio value={SORTING_ORDER.CUSTOM}>
                  <div className={classNames(styles.customRadioGroupOption)}>
                    Custom
                    <FaRegQuestionCircle
                      className={classNames(styles.faQuestionIcon)}
                      title={CUSTOM_SORT_TOOLTIP}
                      color="gray"
                    />
                  </div>
                </Radio>
              )}
            </HStack>
          </RadioGroup>
        </Box>
        <Button
          data-testid={ELEMENT_DATA_TEST_IDS.DELETE_RULE_BUTTON}
          isDisabled={disableDelete}
          rightIcon={<FaTrashCan />}
          variant={'ghost'}
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(selectedRule.columnValue);
          }}
        />
      </HStack>
    </Box>
  );
};

export default ViewLayoutSortableItem;
